import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsivePie } from '@nivo/pie';
import { opacify } from 'polished';
import { DirectionsCar, DirectionsWalk } from '@mui/icons-material';
import PropTypes from 'prop-types';

import { useStyles } from './CommonStyles.styles';

import { CenteredMetric } from '../../../utils/ChartUtils/chart-util';
import { Loading } from '../Loading';
import { Error } from '../Error';
import { Filter } from './Filter';
import { getSales } from '../../../services/ReportService';
import { useMenuContext } from '../../../context/menuContext';
import { formatCurrencyDisplay } from '../../../utils/FormatUtils/formatCurrencyValue';

export function Type({ timeFilter }) {
  const classes = useStyles();
  const [filter, setFilter] = useState('Total Sales');
  const { selectedRestaurant } = useMenuContext();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorRequestId, setErrorRequestId] = useState(null);

  const infoType = useMemo(
    () =>
      ({
        'Total Sales': 'sales',
        'Total Wholesale': 'wholesale',
        'Total In-House': 'inhouse',
        'Total Profit': 'profit',
      }[filter]),
    [filter]
  );

  useEffect(() => {
    if (selectedRestaurant?.id)
      (async () => {
        try {
          setError(false);
          setErrorRequestId(null);
          setLoading(true);
          const response = await getSales(
            selectedRestaurant.id,
            timeFilter,
            'sales-type'
          );
          setData(response);
        } catch (err) {
          setErrorRequestId(
            err?.requestId || err?.__apiContext?.requestId || null
          );
          setError(true);
        } finally {
          setLoading(false);
        }
      })();
  }, [selectedRestaurant, timeFilter]);

  useEffect(() => {});

  return (
    <>
      <Box className={classes.containerTitleWrapper}>
        <Typography className={classes.outsideContainerTitle}>
          By Type
        </Typography>
        <Filter filter={filter} setFilter={setFilter} />
      </Box>
      <Box
        className={classes.infoContainer}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            {error ? (
              <Error requestId={errorRequestId} />
            ) : (
              <>
                <Box
                  display="flex"
                  marginBottom="32px"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography variant="h1">
                    {formatCurrencyDisplay(data[infoType].total)}
                  </Typography>
                  <Box>
                    <Box
                      display="flex"
                      marginBottom="12px"
                      justifyContent="end"
                    >
                      <Typography
                        style={{
                          color: opacify(-0.4, '#282B31'),
                          fontSize: '12px',
                          fontWeight: '500',
                          marginRight: 5,
                        }}
                      >
                        {formatCurrencyDisplay(
                          data[infoType]?.chart[0]?.total || 0
                        )}
                      </Typography>
                      <DirectionsWalk
                        style={{
                          fontSize: '16px',
                          fill: opacify(-0.4, '#282B31'),
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="end">
                      <Typography
                        style={{
                          color: opacify(-0.4, '#282B31'),
                          fontSize: '12px',
                          fontWeight: '500',
                          marginRight: 5,
                        }}
                      >
                        {formatCurrencyDisplay(
                          data[infoType]?.chart[1]?.total || 0
                        )}
                      </Typography>
                      <DirectionsCar
                        style={{
                          fontSize: '16px',
                          fill: opacify(-0.4, '#282B31'),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box style={{ height: '300px', width: '100%' }}>
                  <ResponsivePie
                    data={data[infoType].chart}
                    colors={['#6854FF', '#FF82BA']}
                    innerRadius={0.75}
                    cornerRadius={4}
                    enableRadialLabels={false}
                    enableSliceLabels={false}
                    margin={{ bottom: 56 }}
                    layers={[
                      'arcs',
                      'arcLabels',
                      'radialLabels',
                      'legends',
                      CenteredMetric,
                    ]}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: 56,
                        itemsSpacing: 0,
                        itemWidth: 100,
                        itemHeight: 18,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: 18,
                        symbolShape: 'circle',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000',
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
}

Type.propTypes = {
  timeFilter: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
  }),
};

export default Type;
